import { zodResolver } from '@hookform/resolvers/zod';
import { Grid } from '@material-ui/core';
import { Tabs, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedCheckbox from '@portal/components/AdvancedCheckbox/AdvancedCheckbox';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import PermissionsCard from '@portal/components/PermissionsCard/PermissionsCard';
import { getRouteStackPath } from '@portal/config/routes';
import { translate } from '@portal/services/i18n';

import { UserActions, UserImpersonationActions } from '@portal/actions';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import {
  cards,
  employees,
  multiUser,
  myAccount,
  payment,
  payslip,
  pix,
  transfer,
} from '@portal/config/app.json';
import useReduxState from '@portal/hooks/useReduxState';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import { PermissionsCardType } from '@portal/models/enumerators/cards';
import { ServiceFeature } from '@portal/models/enumerators/employees';
import { isCNPJ } from '@portal/services/masks';
import { useDispatch } from 'react-redux';

enum InviteUserTabs {
  LOGIN = '1',
  PERMISSIONS = '2',
}

interface PermissionCardConfig {
  type: PermissionsCardType;
  titleKey: string;
  condition: boolean;
}

import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import { useNavigation } from '@portal/hooks/useNavigation';
import * as Sentry from '@sentry/react';
import './InviteUser.less';

const { Text } = Typography;
const { TabPane } = Tabs;

const inviteUserSchema = z.object({
  impersonatorUserLogin: z
    .string()
    .min(1, translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_LOGIN_REQUIRED')),
  permissions: z
    .array(z.string())
    .min(
      1,
      translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_PERMISSIONS_REQUIRED')
    ),
});

type InviteUserForm = z.infer<typeof inviteUserSchema>;

const InviteUser = () => {
  const navigate = useNavigation();
  const dispatch = useDispatch();

  if (multiUser.hideInviteAndAccountSelect) {
    navigate(getRouteStackPath('PROFILE', 'MULTI_ACCESS'));
    return null;
  }

  const [activeTab, setActiveTab] = useState<InviteUserTabs>(
    InviteUserTabs.LOGIN
  );
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [allowedToGoPermissions, setAllowedToGoPermissions] = useState(false);

  const {
    control,
    handleSubmit: handleFormSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<InviteUserForm>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: {
      impersonatorUserLogin: '',
      permissions: [],
    },
    mode: 'onChange',
  });

  const impersonatorUserLogin = watch('impersonatorUserLogin');
  const permissions = watch('permissions');

  const {
    user: { permissionsList, me },
  } = useReduxState();

  const hasMatchingPermissions = (type: PermissionsCardType) => {
    return permissionsList?.filter((item) => type === item.type).length > 0;
  };

  useEffect(() => {
    dispatch(UserActions.availablePermissions());
  }, [dispatch]);

  const handlePermissionChange = (permission: string) => {
    const currentPermissions = permissions || [];
    const newPermissions = currentPermissions.includes(permission)
      ? currentPermissions.filter((p) => p !== permission)
      : [...currentPermissions, permission];
    setValue('permissions', newPermissions);
  };

  const allowEmployees = me?.account?.financialFeature?.some(
    (account) => account.serviceType === ServiceFeature.ALLOW_PAYMENT
  );

  const onSubmit = async (data: InviteUserForm) => {
    setLoading(true);

    try {
      await dispatch(
        UserImpersonationActions.requestUserImpersonation({
          impersonatorUserLogin: data.impersonatorUserLogin,
          permissions: data.permissions,
        })
      );

      navigate(getRouteStackPath('PROFILE', 'MULTI_ACCESS'));
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'InviteUser.requestUserImpersonation',
          impersonatorUserLogin: data.impersonatorUserLogin,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const canProceedToPermissions = impersonatorUserLogin?.trim().length > 0;
  const canSubmit =
    impersonatorUserLogin?.trim().length > 0 && permissions?.length > 0;

  const permissionCardsConfig: PermissionCardConfig[] = [
    {
      type: PermissionsCardType.TRANSFER,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.TRANSFER',
      condition: true,
    },
    {
      type: PermissionsCardType.PIX,
      titleKey: 'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.PIX',
      condition: pix.enabled,
    },
    {
      type: PermissionsCardType.BILLET,
      titleKey: 'ENUMERATORS.TRANSACTION_TYPE.BILLET.TEXT',
      condition: myAccount.billetLimit,
    },
    {
      type: PermissionsCardType.TED,
      titleKey: 'ENUMERATORS.TRANSACTION_TYPE.TED.TEXT',
      condition: transfer.ted,
    },
    {
      type: PermissionsCardType.P2P,
      titleKey: 'ENUMERATORS.TRANSACTION_TYPE.P2P.TEXT',
      condition: transfer.p2p,
    },
    {
      type: PermissionsCardType.CARDS,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.CARDS',
      condition: cards.enabled,
    },
    {
      type: PermissionsCardType.PROFILE,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.PROFILE',
      condition: true,
    },
    {
      type: PermissionsCardType.PAYABLE,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.PAYABLE',
      condition: payment.accountPayable,
    },
    {
      type: PermissionsCardType.RECEIVABLE,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.RECEIVABLE',
      condition: payment.accountReceivable,
    },
    {
      type: PermissionsCardType.PAYSLIP,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.PAYSLIP',
      condition:
        me.account.type === AccountTaxIdentifierType.CORPORATE &&
        !!payslip.enabled,
    },
    {
      type: PermissionsCardType.EMPLOYEES,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.EMPLOYEES',
      condition: employees.enabled && !!allowEmployees,
    },
  ];

  const tabOptions = [
    {
      label: translate(
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.TABS.LOGIN.TITLE'
      ),
      value: InviteUserTabs.LOGIN,
      children: (
        <>
          <div className="invite-user-page__input">
            <Controller
              name="impersonatorUserLogin"
              control={control}
              render={({ field }) => (
                <>
                  <AdvancedInput
                    label={translate(
                      'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.TABS.LOGIN.LABEL'
                    )}
                    placeholder={translate(
                      'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.TABS.LOGIN.PLACEHOLDER'
                    )}
                    value={field.value}
                    onChange={(val) => {
                      setErrorMessage(false);
                      field.onChange(val);
                    }}
                    required
                    error={!!errors.impersonatorUserLogin?.message}
                    maxLength={14}
                  />
                  {errorMessage ? (
                    <Text type="danger">
                      {translate(
                        'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.TABS.LOGIN.ERROR_MESSAGE'
                      )}
                    </Text>
                  ) : null}
                </>
              )}
            />
          </div>
          <div className="invite-user-page__button">
            <AdvancedButton
              text={translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.BUTTONS.CONTINUE'
              )}
              onClick={() => handleNextTab()}
              disabled={!canProceedToPermissions}
              size="large"
            />
          </div>
        </>
      ),
    },
    {
      label: translate(
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.TABS.PERMISSIONS.TITLE'
      ),
      value: InviteUserTabs.PERMISSIONS,
      disable: !allowedToGoPermissions,
      children: (
        <div className="invite-user-page__permissions">
          <Text className="invite-user-page__permissions__title">
            {translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.TABS.PERMISSIONS.DESCRIPTION'
            )}
          </Text>
          <Grid xs={12} container spacing={2}>
            {permissionCardsConfig
              .filter(
                (config) =>
                  config.condition && hasMatchingPermissions(config.type)
              )
              .map((config) => (
                <Grid
                  key={config.type}
                  item
                  xs={12}
                  sm={12}
                  md={6}
                  lg={4}
                  xl={3}
                  alignItems={'stretch'}
                >
                  <PermissionsCard
                    title={translate(config.titleKey)}
                    type={config.type}
                    onChange={handlePermissionChange}
                    selectedRoles={permissions || []}
                  />
                </Grid>
              ))}
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={4}
              xl={3}
              alignItems={'stretch'}
            >
              <PermissionsCard
                title={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.GENERAL'
                )}
                type={PermissionsCardType.ALL}
                onChange={handlePermissionChange}
                selectedRoles={permissions || []}
              >
                <AdvancedCheckbox
                  label={translate(
                    'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.ALL_PERMISSIONS'
                  )}
                  value={permissions?.length === permissionsList?.length}
                  onChange={() => {
                    if (permissions?.length === permissionsList?.length) {
                      setValue('permissions', []);
                    } else {
                      setValue(
                        'permissions',
                        permissionsList?.map((item) => item.value || '') || []
                      );
                    }
                  }}
                />
              </PermissionsCard>
            </Grid>
          </Grid>
          <div className="invite-user-page__permissions__buttons">
            <AdvancedButton
              text={translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.BUTTONS.BACK'
              )}
              onClick={() => {
                setAllowedToGoPermissions(false);
                setActiveTab(InviteUserTabs.LOGIN);
              }}
              variant="outlined"
              size="large"
            />
            <AdvancedButton
              text={translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.BUTTONS.SEND_INVITE'
              )}
              onClick={handleFormSubmit(onSubmit)}
              loading={loading}
              disabled={!canSubmit}
              size="large"
            />
          </div>
        </div>
      ),
    },
  ];

  const handleNextTab = () => {
    const value = impersonatorUserLogin?.trim();
    const isOnlyNumbers = /^[0-9]+$/.test(value);

    if (isOnlyNumbers) {
      const isCnpjValid = isCNPJ(value);

      if (!isCnpjValid) {
        setErrorMessage(true);
        setAllowedToGoPermissions(false);
        return;
      }
    }

    setErrorMessage(false);
    setAllowedToGoPermissions(true);
    setActiveTab(InviteUserTabs.PERMISSIONS);
  };

  return (
    <>
      <AdvancedHeader
        title={translate('APPLICATION.TITLE')}
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.BREADCRUMB.HOME'
            ),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.BREADCRUMB.ACCOUNT'
            ),
            url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
          },
          {
            active: true,
            title: translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.BREADCRUMB.MULTI_ACCESS'
            ),
            url: getRouteStackPath('PROFILE', 'MULTI_ACCESS'),
          },
          {
            active: false,
            title: translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.BREADCRUMB.INVITE_USER'
            ),
          },
        ]}
      />
      <div className="invite-user-page">
        <AdvancedPanelContentHeader
          testId="invite-user-header"
          title={translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.HEADER.TITLE'
          )}
          description={translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER_PAGE.HEADER.DESCRIPTION'
          )}
          goBackAction={() => navigate.back()}
        />
        <div className="invite-user">
          <AdvancedCard testId="invite-user-card">
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as InviteUserTabs)}
              type="card"
            >
              {tabOptions.map((tab) => (
                <TabPane tab={tab.label} key={tab.value} disabled={tab.disable}>
                  {tab.children}
                </TabPane>
              ))}
            </Tabs>
          </AdvancedCard>
        </div>
      </div>
    </>
  );
};

export default InviteUser;
